import React, { useLayoutEffect, useRef, useState } from 'react';

import type { TooltipPlacement } from 'antd/es/tooltip';

import { Tooltip } from '@/shared/ui/Tooltip';

import styles from './EllipsisText.module.css';

type EllipsisTextProps = {
  text: string;
  className?: string;
  tooltipPlacement?: TooltipPlacement;
  children?: React.ReactNode;
  arrow?: boolean;
};

export const EllipsisText: React.FC<EllipsisTextProps> = ({
  text,
  className,
  arrow: _arrow = false,
  tooltipPlacement = 'topLeft',
  children,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const measure = () => {
      const width = element.offsetWidth;
      if (width === 0) {
        return;
      }

      element.style.overflow = 'visible';
      element.style.textOverflow = 'clip';
      element.style.whiteSpace = 'nowrap';

      const scrollWidth = element.scrollWidth;
      const isContentOverflowing = scrollWidth - width > 1;

      element.style.overflow = 'hidden';
      element.style.textOverflow = 'ellipsis';
      element.style.whiteSpace = 'nowrap';

      setIsOverflowing(isContentOverflowing);
    };

    measure();
    const raf = requestAnimationFrame(measure);

    const ro = new ResizeObserver(() => {
      measure();
    });
    ro.observe(element);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [text, children]);

  const ellipsisStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  return (
    <Tooltip
      getPopupContainer={() => document.body}
      title={isOverflowing ? text : undefined}
      placement={tooltipPlacement}
      rootClassName={isOverflowing ? styles.tooltip : undefined}>
      <span ref={textRef} className={className} style={ellipsisStyle} aria-label={isOverflowing ? text : undefined}>
        {children || text}
      </span>
    </Tooltip>
  );
};
